import { useState } from 'react';
import type { TopPage, EntryPage, ExitPage, EventCount, FilterKey } from '@/types';

type PageTab = 'all' | 'entry' | 'exit' | 'events';

interface TopPagesProps {
  pages: TopPage[];
  entryPages: EntryPage[];
  exitPages: ExitPage[];
  customEvents?: EventCount[];
  isLoading?: boolean;
  onFilter?: (key: FilterKey, value: string) => void;
  onShowMore?: (tab: PageTab) => void;
  hasMore?: boolean;
}

export function TopPages({ pages, entryPages, exitPages, customEvents, isLoading, onFilter, onShowMore, hasMore }: TopPagesProps) {
  const [activeTab, setActiveTab] = useState<PageTab>('all');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-6 w-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex border-b border-gray-200 mb-3">
        {(['all', 'entry', 'exit', 'events'] as PageTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'all' ? 'All Pages' : tab === 'entry' ? 'Entry Pages' : tab === 'exit' ? 'Exit Pages' : 'Events'}
          </button>
        ))}
      </div>

      {activeTab === 'all' && <AllPagesView pages={pages} onFilter={onFilter} />}
      {activeTab === 'entry' && <EntryPagesView pages={entryPages} onFilter={onFilter} />}
      {activeTab === 'exit' && <ExitPagesView pages={exitPages} onFilter={onFilter} />}
      {activeTab === 'events' && <EventsView events={customEvents || []} />}

      {hasMore && activeTab !== 'events' && (
        <button
          onClick={() => onShowMore?.(activeTab)}
          className="w-full mt-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
        >
          Show more
        </button>
      )}
    </div>
  );
}

function AllPagesView({ pages, onFilter }: { pages: TopPage[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (pages.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No page data available</div>;
  }

  const total = pages.reduce((sum, p) => sum + p.views, 0);
  const maxViews = Math.max(...pages.map((p) => p.views));

  return (
    <div className="space-y-2">
      {pages.map((page) => {
        const percentage = total > 0 ? (page.views / total) * 100 : 0;
        const barWidth = maxViews > 0 ? (page.views / maxViews) * 100 : 0;

        return (
          <div
            key={page.path}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('page_path', page.path)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{page.path}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="text-gray-500 text-xs">{page.visitors.toLocaleString()} visitors</span>
              <span className="font-medium w-12 text-right">{page.views.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntryPagesView({ pages, onFilter }: { pages: EntryPage[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (pages.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No entry page data available</div>;
  }

  const total = pages.reduce((sum, p) => sum + p.entries, 0);
  const maxEntries = Math.max(...pages.map((p) => p.entries));

  return (
    <div className="space-y-2">
      {pages.map((page) => {
        const percentage = total > 0 ? (page.entries / total) * 100 : 0;
        const barWidth = maxEntries > 0 ? (page.entries / maxEntries) * 100 : 0;

        return (
          <div
            key={page.path}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('page_path', page.path)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{page.path}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="text-gray-500 text-xs">{page.visitors.toLocaleString()} visitors</span>
              <span className="font-medium w-12 text-right">{page.entries.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExitPagesView({ pages, onFilter }: { pages: ExitPage[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (pages.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No exit page data available</div>;
  }

  const total = pages.reduce((sum, p) => sum + p.exits, 0);
  const maxExits = Math.max(...pages.map((p) => p.exits));

  return (
    <div className="space-y-2">
      {pages.map((page) => {
        const percentage = total > 0 ? (page.exits / total) * 100 : 0;
        const barWidth = maxExits > 0 ? (page.exits / maxExits) * 100 : 0;

        return (
          <div
            key={page.path}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('page_path', page.path)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{page.path}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="text-gray-500 text-xs">{page.visitors.toLocaleString()} visitors</span>
              <span className="font-medium w-12 text-right">{page.exits.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventsView({ events }: { events: EventCount[] }) {
  if (events.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No event data available</div>;
  }

  const total = events.reduce((sum, e) => sum + e.count, 0);
  const maxCount = Math.max(...events.map((e) => e.count));

  return (
    <div className="space-y-2">
      {events.map((event) => {
        const percentage = total > 0 ? (event.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (event.count / maxCount) * 100 : 0;

        return (
          <div
            key={event.name}
            className="flex items-center justify-between text-sm"
            style={{ minHeight: '32px' }}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{event.name}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="font-medium w-12 text-right">{event.count.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TopPages;
